import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    VStack,
    Checkbox,
    HStack,
} from "@chakra-ui/react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../configs/firebase";
import { useAuth, UserAddress } from "../../auth/AuthContext";
import { v4 as uuidv4 } from "uuid";
import { useState } from "react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import AutocompleteInput from "../../LocationsModule/PlaceAutocompleteInput";

interface Props {
    onDone: () => void;
}

const AddAddressForm: React.FC<Props> = ({ onDone }) => {
    const { user, userSettings, refreshUserSettings } = useAuth();
    const { theme } = useCustomTheme();

    const [label, setLabel] = useState("");
    const [addressLine, setAddressLine] = useState("");
    const [instructions, setInstructions] = useState("");
    const [coords, setCoords] = useState<{ lat: number; lng: number; } | null>(null);

    const [setAsDelivery, setSetAsDelivery] = useState(true);
    const [setAsPickup, setSetAsPickup] = useState(false);

    if (!user || !userSettings) return null;

    const saveAddress = async () => {
        if (!label || !addressLine || !coords) return;

        const existing = userSettings.addresses ?? [];

        const updatedExisting = existing.map((a) => ({
            ...a,
            isCurrent: setAsDelivery ? false : a.isCurrent,
            isPickup: setAsPickup ? false : a.isPickup,
        }));

        const newAddress: UserAddress = {
            id: uuidv4(),
            label,
            addressLine,
            instructions,
            latitude: coords.lat,
            longitude: coords.lng,
            createdAt: Date.now(),
            isCurrent: setAsDelivery,
            isPickup: setAsPickup,
        };

        await updateDoc(doc(db, "users", user.uid), {
            addresses: [...updatedExisting, newAddress],
        });

        await refreshUserSettings();
        onDone();
    };

    return (
        <Box mt={4}>
            <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                    <FormLabel color={theme.text}>Label</FormLabel>
                    <Input
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="Home / Office / Shop"
                        bg={theme.inputBg}
                        color={theme.text}
                        borderColor={theme.inputBorder}
                        _placeholder={{ color: theme.inputPlaceholder }}
                        _hover={{ borderColor: theme.inputBorderFocus }}
                        _focus={{
                            borderColor: theme.inputBorderFocus,
                            boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                        }}
                    />
                </FormControl>

                <FormControl isRequired>
                    <FormLabel color={theme.text}>Address</FormLabel>
                    <AutocompleteInput
                        placeholder="Search delivery address"
                        onPlaceSelected={setAddressLine}
                        onLocationSelected={setCoords}
                    />
                </FormControl>
                <FormControl>
                    <FormLabel color={theme.text}>Instructions</FormLabel>
                    <Input
                        value={instructions}
                        onChange={(e) => setInstructions(e.target.value)}
                        placeholder="Optional"
                        bg={theme.inputBg}
                        color={theme.text}
                        borderColor={theme.inputBorder}
                        _placeholder={{ color: theme.inputPlaceholder }}
                        _hover={{ borderColor: theme.inputBorderFocus }}
                        _focus={{
                            borderColor: theme.inputBorderFocus,
                            boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                        }}
                    />
                </FormControl>

                <HStack spacing={6} color={theme.text}>
                    <Checkbox
                        colorScheme="teal"
                        isChecked={setAsDelivery}
                        onChange={(e) => setSetAsDelivery(e.target.checked)}
                    >
                        Delivery address
                    </Checkbox>

                    <Checkbox
                        colorScheme="purple"
                        isChecked={setAsPickup}
                        onChange={(e) => setSetAsPickup(e.target.checked)}
                    >
                        Pickup address
                    </Checkbox>
                </HStack>

                <Button
                    color={theme.textOnPrimary}
                    bgImage={theme.gradients.primaryButton}
                    _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                    boxShadow={theme.shadowSm}
                    onClick={saveAddress}
                    isDisabled={!label || !addressLine || !coords}
                    w={{ base: "100%", sm: "auto" }}
                    alignSelf="flex-start"
                >
                    Save address
                </Button>
            </VStack>
        </Box>
    );
};

export default AddAddressForm;